/**
 * Multiplexed Yjs sync protocol over a single WebSocket.
 *
 * Every frame is binary:
 *   varUint  messageType
 *   varString docKey            ("meta" | "note:<id>")
 *   bytes    payload            (type-specific)
 *
 * MSG_SYNC       payload = y-protocols/sync message (step1/step2/update).
 * MSG_AWARENESS  payload = y-protocols/awareness update for that doc.
 * MSG_SUBSCRIBE  client->server, empty payload: register interest in docKey.
 *                Server replies with its syncStep1 for the doc; the client
 *                also sends its own syncStep1 right after subscribing, so
 *                both sides converge (standard y-websocket handshake, but
 *                per-doc on one socket).
 * MSG_UNSUBSCRIBE client->server, empty payload.
 *
 * Doc lifecycle: the client learns the doc list from the meta doc and
 * subscribes to every note doc (full-vault replication). New docs are
 * created client-side (note creation) or server-side (watcher import); both
 * propagate through the meta doc, after which the other side subscribes.
 */
import * as encoding from 'lib0/encoding';
import * as decoding from 'lib0/decoding';

export const MSG_SYNC = 0;
export const MSG_AWARENESS = 1;
export const MSG_SUBSCRIBE = 2;
export const MSG_UNSUBSCRIBE = 3;

export interface Frame {
  type: number;
  docKey: string;
  payload: Uint8Array;
}

export function encodeFrame(type: number, docKey: string, payload?: Uint8Array): Uint8Array {
  const enc = encoding.createEncoder();
  encoding.writeVarUint(enc, type);
  encoding.writeVarString(enc, docKey);
  if (payload && payload.length > 0) encoding.writeUint8Array(enc, payload);
  return encoding.toUint8Array(enc);
}

export function decodeFrame(data: Uint8Array): Frame {
  const dec = decoding.createDecoder(data);
  const type = decoding.readVarUint(dec);
  const docKey = decoding.readVarString(dec);
  const payload = data.slice(dec.pos);
  return { type, docKey, payload };
}
